import type { SensorWithContext, TelemetryPoint } from '@monitor/shared';
import { dayjs } from '@/lib/dayjs';

// ── Realtime time-axis helpers (shared by the dashboard power chart + the
//    zone/board comparison chart + the pop-out window) ──────────────────────
export type ChartWindow = '30s' | '1m' | '5m' | '15m' | '30m' | '1h';
export const CHART_TICK_MS: Record<ChartWindow, number> = {
  '30s': 30_000,
  '1m':  60_000,
  '5m':  5 * 60_000,
  '15m': 15 * 60_000,
  '30m': 30 * 60_000,
  '1h':  60 * 60_000,
};
export const SPAN_STEPS = 6;

// Snap the axis min/max to multiples of tickMs so gridlines land on whole clock
// times. Second-resolution label only when the tick step is sub-minute.
export function axisTicks(tickMs: number, nowMs: number) {
  const fmt = tickMs < 60_000 ? 'HH:mm:ss' : 'HH:mm';
  const min = Math.floor((nowMs - SPAN_STEPS * tickMs) / tickMs) * tickMs;
  const max = Math.ceil(nowMs / tickMs) * tickMs;
  return { tickMs, fmt, min, max };
}

// ── Zone/board comparison ──────────────────────────────────────────────────
export type CompareBy = 'zone' | 'board';
export type CompareMetric = 'power' | 'voltage' | 'current';

export const COMPARE_COLORS = ['#22d3ee', '#a78bfa', '#4ade80', '#f59e0b', '#f472b6', '#38bdf8', '#facc15', '#fb7185', '#34d399', '#c084fc', '#60a5fa', '#f87171'];

export const COMPARE_METRIC_META: Record<CompareMetric, { unit: string; dp: number; avg: boolean; label: string }> = {
  voltage: { unit: 'V', dp: 1, avg: true,  label: 'แรงดัน' },   // a level → average across a group
  current: { unit: 'A', dp: 3, avg: false, label: 'กระแส' },    // additive → sum
  power:   { unit: 'W', dp: 1, avg: false, label: 'กำลังไฟ' },  // additive → sum
};

interface ChartTheme {
  tooltipBg: string; tooltipBorder: string; tooltipText: string;
  label: string; grid: string;
}
interface SensorRealtime { history: TelemetryPoint[]; latest: TelemetryPoint | null }

export interface BuildCompareArgs {
  bySensor: Record<number, SensorRealtime> | undefined;
  activeSensors: SensorWithContext[];
  countedIds: Set<number>;
  zones: Array<{ id: number; code: string; name: string }>;
  boards: Array<{ id: number; code: string }>;
  compareBy: CompareBy;
  metric: CompareMetric;
  chartWindow: ChartWindow;
  now: number;
  ct: ChartTheme;
}

/**
 * Build the ECharts option for the zone/board comparison line chart. One line
 * per zone (or per board): each line is that group's total (W/A) or average (V)
 * of its counted meters, on the same bucket timeline as the main power chart.
 * Pure — safe to call from the dashboard card and the pop-out window alike.
 */
export function buildCompareOption(args: BuildCompareArgs) {
  const { bySensor, activeSensors, countedIds, zones, boards, compareBy, metric, chartWindow, now, ct } = args;
  const meta = COMPARE_METRIC_META[metric];
  const tickMs = CHART_TICK_MS[chartWindow];
  const win = tickMs * SPAN_STEPS;
  const bucketMs = Math.max(2_000, Math.floor(tickMs / 10));
  const nowBucket = Math.floor(now / bucketMs) * bucketMs;
  const startBucket = nowBucket - Math.floor(win / bucketMs) * bucketMs;

  const keyOf = (s: SensorWithContext): string =>
    compareBy === 'zone' ? `z:${s.zoneId ?? 'none'}` : `b:${s.boardId}`;
  const labelOf = (s: SensorWithContext): string => {
    if (compareBy === 'zone') {
      if (s.zoneId == null) return 'ไม่ระบุโซน';
      return zones.find((z) => z.id === s.zoneId)?.code ?? `Zone ${s.zoneId}`;
    }
    return boards.find((b) => b.id === Number(s.boardId))?.code ?? `Board ${s.boardId}`;
  };

  const byTime = new Map<number, Map<string, Map<number, { sum: number; count: number }>>>();
  const groups = new Map<string, string>(); // key → label
  if (bySensor) {
    for (const s of activeSensors) {
      if (!countedIds.has(s.id)) continue;
      const gk = keyOf(s);
      if (!groups.has(gk)) groups.set(gk, labelOf(s));
      const state = bySensor[s.id];
      if (!state) continue;
      for (const p of state.history) {
        const ts = new Date(p.time).getTime();
        if (ts < startBucket) continue;
        const v = p[metric];
        if (v == null) continue;
        const bucket = Math.floor(ts / bucketMs) * bucketMs;
        const perGroup = byTime.get(bucket) ?? new Map<string, Map<number, { sum: number; count: number }>>();
        const perSensor = perGroup.get(gk) ?? new Map<number, { sum: number; count: number }>();
        const e = perSensor.get(s.id) ?? { sum: 0, count: 0 };
        e.sum += v;
        e.count += 1;
        perSensor.set(s.id, e);
        perGroup.set(gk, perSensor);
        byTime.set(bucket, perGroup);
      }
    }
  }

  const keys = [...groups.keys()];
  let hasData = false;
  const series = keys.map((gk, i) => {
    const color = COMPARE_COLORS[i % COMPARE_COLORS.length];
    const data: [number, number | null][] = [];
    for (let t = startBucket; t <= nowBucket; t += bucketMs) {
      const perSensor = byTime.get(t)?.get(gk);
      if (!perSensor) { data.push([t, null]); continue; }
      let total = 0, n = 0;
      for (const e of perSensor.values()) { if (e.count) { total += e.sum / e.count; n += 1; } }
      if (n === 0) { data.push([t, null]); continue; }
      hasData = true;
      data.push([t, meta.avg ? total / n : total]);
    }
    return {
      // connectNulls: bridge empty buckets so the line stays continuous. The
      // history backfill is only 1-minute resolution, so on sub-15-min windows
      // (finer buckets) most buckets are empty until realtime fills them — with
      // gaps the chart looked "not loaded". Bridging keeps the comparison
      // readable; live 3-second data still fills the recent edge densely.
      name: groups.get(gk)!, type: 'line', smooth: true, symbol: 'none',
      connectNulls: true, sampling: 'lttb',
      lineStyle: { color, width: 2 }, itemStyle: { color },
      data,
    };
  });

  const { tickMs: tk, fmt: xFmt, min: xMin, max: xMax } = axisTicks(CHART_TICK_MS[chartWindow], now);
  const option = {
    grid: { left: 52, right: 16, top: 34, bottom: 26 },
    legend: { top: 0, type: 'scroll', textStyle: { color: ct.label, fontSize: 11 }, icon: 'roundRect', itemWidth: 14, itemHeight: 8, pageIconColor: ct.label, pageTextStyle: { color: ct.label } },
    tooltip: {
      trigger: 'axis', backgroundColor: ct.tooltipBg, borderColor: ct.tooltipBorder, textStyle: { color: ct.tooltipText, fontSize: 12 },
      formatter: (params: Array<{ axisValue: number; seriesName: string; marker: string; value: [number, number | null] }>) => {
        const head = dayjs(params[0]?.axisValue).format('HH:mm:ss');
        const lines = params
          .filter((p) => p.value?.[1] != null)
          .sort((a, b) => (b.value[1] ?? 0) - (a.value[1] ?? 0))
          .map((p) => `${p.marker} ${p.seriesName}: ${Number(p.value[1]).toFixed(meta.dp)} ${meta.unit}`);
        return [head, ...(lines.length ? lines : ['ไม่มีข้อมูล'])].join('<br/>');
      },
    },
    xAxis: { type: 'value', min: xMin, max: xMax, interval: tk, axisLabel: { color: ct.label, fontSize: 10, hideOverlap: true, formatter: (v: number) => dayjs(v).format(xFmt) }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
    yAxis: { type: 'value', name: meta.unit, nameTextStyle: { color: ct.label, fontSize: 10 }, axisLabel: { color: ct.label, fontSize: 10 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: ct.grid } } },
    series,
  };
  return { option, hasData, count: keys.length };
}
